import React, { FC, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid, Button, TextField, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store';
import FileUploader from '../../file-loader';
import FileTypeSelect from './components/file-type-select';
import useStyles from './styles';
import { FileTypeConfig } from '../../../entities/file-type-configurations-v1/models';
import EnhancedTable from '../../table-old';
import { headCells } from './head-cell';
import ProgressBar from '../../progress-bar';
import {
  getFileTypeOptions,
  getAllConfigFileStatuses,
  pushConfigFile,
  shortMessageRequestConfig,
  deleteConfigFiles,
  downloadConfigFile,
} from '../../../store/commands/configuration-files/actions';
import DeleteIcon from '../../../assets/icons/delete-icon';
import DownloadIcon from '../../../assets/icons/download-icon';

type SelectOption = {
  id: string;
  label: string;
};
const formatRowData = (data: any, index: number) => {
  if (data?.commands && data.commands.length > 0) {
    return {
      ...data,
      id: index,
      commands: [
        {
          ...data.commands[0],
          status: data.commands[0].status === 'UNBOUND' ? '' : data.commands[0].status,
        },
        ...data.commands.slice(1),
      ],
    };
  }
  return { ...data, id: index };
};

const ConfigurationFiles: FC = () => {
  const login = useSelector((state: AppState) => state.login);
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const classes = useStyles();
  const [fileTypeOptions, setFileTypeOptions] = useState<SelectOption[] | undefined>();
  const [pushFileTypeValue, setPushFileTypeValue] = useState<string>('');
  const [retrievableFileTypeValue, setRetrievableFileTypeValue] = useState<string>('');
  const [pushFileName, setPushFileName] = useState<string>('');
  const [fileToPush, setFileToPush] = useState<any>();
  const [tableData, setTableData] = useState<any[] | []>([]);
  const [partNumber, setPartNumber] = useState<string>('');
  const [percentUpload, setPercentUpload] = useState<number>(0);
  const [selectedCommands, setSelectedCommands] = useState([]);
  const [retrievableFileTypeValueToDelete, setRetrievableFileTypeValueToDelete] = useState<string>('');
  const [requestFileTypeValue,setRequestFileTypeValue] = useState<string>('');
  const [deleteFileTypeValue,setDeleteFileTypeValue] = useState<string>('');
  const [queueFileTypeValue,setQueueFileTypeValue] = useState<string>('');

  const dispatch = useDispatch();

  const getOptions = useCallback(async () => {
    const fileTypes: any = await dispatch(getFileTypeOptions());
    let fileTypesFT = [];
    if (fileTypes) {
      fileTypesFT = fileTypes.filter((f: any) => f.isPushEnabled);
      const fileTypesSorted: SelectOption[] = [...fileTypesFT].sort((a: any, b: any) => {
        const aSortBy = a.fileTypeName.toLowerCase();
        const bSortBy = b.fileTypeName.toLowerCase();
        let comparison = 0;
        if (aSortBy > bSortBy) {
          comparison = 1;
        } else if (aSortBy < bSortBy) {
          comparison = -1;
        }
        return comparison;
      }).map((f) => ({
        id: `${f.fileTypeId}`,
        label: `${f.fileTypeName}`,
        fileTypeId: `${f.fileTypeId}`,
        fileTypeName: `${f.fileTypeName}`,
      }));
      setFileTypeOptions(fileTypesSorted);
    }
    return fileTypesFT;
  }, [login]);

  const getStatuses = useCallback(async () => {
    const configFileStatuses: any = await dispatch(getAllConfigFileStatuses());
    const fileType = fileTypeOptions || (await getOptions());
    const statusesWithNames = configFileStatuses.map((configFileStatus: { fileTypeId: any }) => {
      const foundType = fileType?.find(
        (fileTypeOption: { fileTypeId: any }) =>
          fileTypeOption.fileTypeId === configFileStatus.fileTypeId,
      );
      return {
        ...configFileStatus,
        fileTypeName: foundType?.fileTypeName,
      };
    }).filter((file: any) => file.commands[0]?.status !== 'UNBOUND' && file.commands.length > 0);
      setTableData(statusesWithNames.map((data: any, index: number) => formatRowData(data, index)));  
  }, [login, selectedEntity, fileTypeOptions, getOptions]);

  const handleDeleteFiles = async (cmds: string[], rows: Array<any>) => {
    const configs = rows
      .filter((command) => cmds.findIndex((c) => c === command.id) !== -1)
      .map((c) => ({
        make: c.make,
        serialNumber: c.serialNumber,
        deviceCharacterization: c.deviceCharacterization,
        fileTypeId: c.fileTypeId,
        transactionId: c.transactionId,
      }));
    if (selectedEntity) {
      const deleted: any = await dispatch(deleteConfigFiles({ configs }));
      if (deleted) {
        const newData = rows
          .filter((row) => !cmds.includes(row.id))
        setSelectedCommands([]);
        setTableData(newData);
      }
    }
  };

  useEffect(() => {
    getStatuses();
  }, [selectedEntity]);

  const fileHandler = async (file: any) => {
    if (file) {
      setPushFileName(file.name);
      setFileToPush(file);
    }
  };

  const addToQueue = async () => {
    if (selectedEntity) {
      await dispatch(
        pushConfigFile({
          file: fileToPush,
          fileTypeId: pushFileTypeValue,
          setPercentUpload,
          partNumber,
        }),
      );
      getStatuses();
      setFileToPush(undefined);
      setPushFileName('');
      setPushFileTypeValue('');
      setPartNumber('');
      setQueueFileTypeValue('');
    }
  };

  const requestFile = async () => {
    if (retrievableFileTypeValue) {
      await dispatch(
        shortMessageRequestConfig({
          fileTypeId: retrievableFileTypeValue,
        }),
      );
      getStatuses();
      setRetrievableFileTypeValue('');
      setRequestFileTypeValue('');
    }
  };
  const handlePartNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPartNumber(event?.target?.value);
  };

  const disableRequestFile = !(
    partNumber.length > 0 &&
    pushFileTypeValue.length > 0 &&
    pushFileTypeValue !== 'select' &&
    !!fileToPush
  );
  const downloadIcon = <DownloadIcon />;
  const downloadFileClick = async (event: any, rowIndex: any) => {
    await dispatch(
      await downloadConfigFile({
        fileName: tableData[rowIndex].fileName,
        fileTypeId: tableData[rowIndex].fileTypeId,
        hash: tableData[rowIndex].hash,
      }),
    );
  };

  const deleteConfigurationFile = async () => {
    if (selectedEntity && retrievableFileTypeValueToDelete) {
      const configs: any = [{
        fileTypeId: retrievableFileTypeValueToDelete,
        deviceKey: selectedEntity?.deviceKey
      }];
      await dispatch(deleteConfigFiles({ configs }));
      getStatuses();
      setRetrievableFileTypeValueToDelete('');
      setDeleteFileTypeValue('');
    }
  }
  return (
    <Grid container direction="row">
      <Grid item xs={12}>
        <Typography variant="h6">Push Configuration File</Typography>
        <Box
          p={1}
          pb={2}
          className={classes.noWrap}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {!percentUpload ? (
            <>
              <Box px={1}>
                <Typography variant="h6">Select File Type:</Typography>
              </Box>
              <Box px={1}>
                <FileTypeSelect
                  selectId="push-config-file-type-select"
                  fileTypeOptions={fileTypeOptions || []}
                  fileTypeValue={pushFileTypeValue}
                  setValue={setPushFileTypeValue}
                  value={queueFileTypeValue}
                  setLabel={setQueueFileTypeValue}
                />
              </Box>
              <Box p={1} pr={0}>
                <Typography variant="h6">File Type#</Typography>
              </Box>
              <Box p={1}>
                <Typography>{pushFileTypeValue}</Typography>
              </Box>
              <Box p={1} mt="-16px">
                <TextField
                  variant="standard"
                  className={classes.partNumber}
                  margin="none"
                  id="part-number-input"
                  label="Part#"
                  onChange={handlePartNumberChange}
                  value={partNumber}
                />
              </Box>
              <Box p={1} pr={0}>
                <FileUploader handleFile={fileHandler} />
              </Box>
              <Box p={1} flexGrow={0} overflow="hidden">
                {pushFileName && (
                  <Typography className={classes.overFlowEllipsis}>{pushFileName}</Typography>
                )}
              </Box>
              <Box display="flex" flexGrow={1} justifyContent="flex-end" pr={2}>
                <Button
                  id="upload-add-to-queue'"
                  onClick={addToQueue}
                  disabled={disableRequestFile}
                  variant="outlined"
                  size="small"
                  color="primary"
                >
                  Add to Queue
                </Button>
              </Box>
            </>
          ) : (
            <>
              <ProgressBar percentUpload={percentUpload} />
            </>
          )}
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">Request Configuration File</Typography>
        <Box
          p={1}
          pb={2}
          className={classes.noWrap}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box p={1}>
            <Typography variant="h6">Select File Type:</Typography>
          </Box>
          <Box p={1}>
            <FileTypeSelect
              selectId="request-config-file-type-select"
              fileTypeOptions={fileTypeOptions || []}
              fileTypeValue={retrievableFileTypeValue}
              setValue={setRetrievableFileTypeValue}
              value={requestFileTypeValue}
              setLabel={setRequestFileTypeValue}
            />
          </Box>
          <Box p={1} pr={0}>
            <Typography variant="h6">File Type#</Typography>
          </Box>
          <Box p={1}>
            <Typography>{retrievableFileTypeValue}</Typography>
          </Box>

          <Box display="flex" flexGrow={1} justifyContent="flex-end" pr={2}>
            <Button
              id="request-file"
              disabled={retrievableFileTypeValue === ''}
              onClick={requestFile}
              variant="outlined"
              size="small"
              color="primary"
            >
              Request File
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Delete Configuration File</Typography>
        <Box
          p={1}
          pb={2}
          className={classes.noWrap}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box p={1}>
            <Typography variant="h6">Select File Type:</Typography>
          </Box>
          <Box p={1}>
            <FileTypeSelect
              selectId="request-config-file-type-select"
              fileTypeOptions={fileTypeOptions || []}
              fileTypeValue={retrievableFileTypeValueToDelete}
              setValue={setRetrievableFileTypeValueToDelete}
              value={deleteFileTypeValue}
              setLabel={setDeleteFileTypeValue}
            />
          </Box>
          <Box p={1} pr={0}>
            <Typography variant="h6">File Type#</Typography>
          </Box>
          <Box p={1}>
            <Typography>{retrievableFileTypeValueToDelete}</Typography>
          </Box>

          <Box display="flex" flexGrow={1} justifyContent="flex-end" pr={2}>
            <Button
              id="request-file"
              disabled={retrievableFileTypeValueToDelete === ''}
              onClick={deleteConfigurationFile}
              variant="outlined"
              size="small"
              color="primary"
            >
              Delete File
            </Button>
          </Box>
        </Box>
      </Grid>
      {selectedCommands.length > 0 && tableData.length > 0 && (
        <Grid item xs={12}>
          <IconButton
            id="deleteButton"
            onClick={() => handleDeleteFiles(selectedCommands, tableData)}
            className={classes.delete}
            aria-label="delete"
            size="large"
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      )}
      {tableData.length > 0 && (
        <Grid item xs={12}>
          <Box mt={2} border={1} borderColor="grey.300">
            <EnhancedTable
              initialSort="modifiedDateTime"
              id="configTable"
              tableHeight={tableData.length > 6 ? `450px` : undefined}
              rows={tableData}
              headCells={headCells}
              expandableData={[]}
              selectableRows={(selection: any) => setSelectedCommands(selection)}
              isDownloadDisabled={false}
              onClickOfDownload={downloadFileClick}
              downloadIconOrMessage={downloadIcon}
              downloadOutline={false}
              downloadStyle={classes.downloadButton}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ConfigurationFiles;